/**
 * Gallery Slider Component
 * Custom element for image gallery slider with arrow navigation and dots
 */
class GallerySlider extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    
    // Cache DOM elements
    this.mask = this.querySelector('.gallery-slider__mask');
    this.slides = Array.from(this.querySelectorAll('.gallery-slider__slide'));
    this.prevButton = this.querySelector('.slider-arrow--prev .slider-arrow__button');
    this.nextButton = this.querySelector('.slider-arrow--next .slider-arrow__button');
    this.dots = Array.from(this.querySelectorAll('.gallery-slider__dot'));
    
    // Initialize state
    this.currentSlide = 0;
    
    // Validate required elements
    if (!this.mask || this.slides.length === 0) return;
    
    // Setup
    this.setupEventListeners();
    this.updateSlidePosition();
    this.updateDots();
  }

  setupEventListeners() {
    // Arrow navigation
    this.prevButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.prev();
    });
    
    this.nextButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.next();
    });
    
    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });
    
    // Responsive handling
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.updateSlidePosition(), 150);
    });
  }

  prev() {
    if (this.currentSlide > 0) {
      this.currentSlide--;
      this.updateSlidePosition();
      this.updateDots();
    }
  }

  next() {
    if (this.currentSlide < this.slides.length - 1) {
      this.currentSlide++;
      this.updateSlidePosition();
      this.updateDots();
    }
  }

  goToSlide(index) {
    this.currentSlide = Math.max(0, Math.min(index, this.slides.length - 1));
    this.updateSlidePosition();
    this.updateDots();
  }

  updateSlidePosition() {
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseFloat(getComputedStyle(this.mask).gap) || 0;
    const offset = -(this.currentSlide * (slideWidth + gap));
    
    this.mask.style.transform = `translateX(${offset}px)`;
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentSlide;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-pressed', isActive.toString());
    });
  }
}

// Register custom element
if (!customElements.get('gallery-slider-component')) {
  customElements.define('gallery-slider-component', GallerySlider);
}

